import { useState, useEffect, useMemo } from 'react';
import type { TermListItem, EntityMatch } from '@acitra/shared';
import { listTerms, matchTerm, unmatchTerm } from '../../services/api';

interface PaymentTermsInfo {
  /** Normalized display string (e.g., "Net 30", "2/10 Net 30") */
  paymentTerms?: string;
  /** Raw text from invoice */
  paymentTermsRaw?: string;
  /** Number of days until payment is due */
  paymentTermsDueDays?: number;
  /** Discount percentage */
  paymentTermsDiscountPercent?: number;
  /** Days to qualify for discount */
  paymentTermsDiscountDays?: number;
}

interface PaymentTermsSelectorProps {
  tenantId: string;
  invoiceId: string;
  extractedTerms: PaymentTermsInfo;
  currentMatch?: EntityMatch;
  onMatchChange: (match: EntityMatch | undefined) => void;
  onOpenCreateModal: () => void;
  /** Newly created term to add to the list optimistically */
  newlyCreatedTerm?: TermListItem;
  disabled?: boolean;
  className?: string; // Add className prop
}

export function PaymentTermsSelector({
  tenantId,
  invoiceId,
  extractedTerms,
  currentMatch,
  onMatchChange,
  onOpenCreateModal,
  newlyCreatedTerm,
  disabled = false,
  className = '', // Default to empty string
}: PaymentTermsSelectorProps) {
  const [terms, setTerms] = useState<TermListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isMatching, setIsMatching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  // Load terms on mount
  useEffect(() => {
    async function loadTerms() {
      try {
        setIsLoading(true);
        const data = await listTerms(tenantId, { active: true });
        setTerms(data);
      } catch (err) {
        console.error('Failed to load terms:', err);
        setError('Failed to load payment terms');
      } finally {
        setIsLoading(false);
      }
    }

    loadTerms();
  }, [tenantId]);

  // Add newly created term to list optimistically
  useEffect(() => {
    if (newlyCreatedTerm && !terms.find((t) => t.id === newlyCreatedTerm.id)) {
      setTerms((prev) => [...prev, newlyCreatedTerm]);
    }
  }, [newlyCreatedTerm, terms]);

  // Find best auto-match based on dueDays
  const suggestedTerm = useMemo(() => {
    if (!extractedTerms.paymentTermsDueDays || terms.length === 0) {
      return null;
    }

    // Find exact match on dueDays
    let match = terms.find((t) => t.dueDays === extractedTerms.paymentTermsDueDays);

    // If we have discount info, try to match more precisely
    if (
      match &&
      extractedTerms.paymentTermsDiscountPercent &&
      extractedTerms.paymentTermsDiscountDays
    ) {
      const betterMatch = terms.find(
        (t) =>
          t.dueDays === extractedTerms.paymentTermsDueDays &&
          t.discountPercent === extractedTerms.paymentTermsDiscountPercent &&
          t.discountDays === extractedTerms.paymentTermsDiscountDays
      );
      if (betterMatch) {
        match = betterMatch;
      }
    }

    return match || null;
  }, [terms, extractedTerms]);

  // Sort terms: suggested first, then alphabetically
  const sortedTerms = useMemo(() => {
    return [...terms].sort((a, b) => {
      // Suggested term first
      if (suggestedTerm?.id === a.id) return -1;
      if (suggestedTerm?.id === b.id) return 1;
      // Then alphabetically
      return a.name.localeCompare(b.name);
    });
  }, [terms, suggestedTerm]);

  const handleSelect = async (term: TermListItem) => {
    // Close dropdown immediately for better UX
    setIsOpen(false);
    setIsMatching(true);
    setError(null);

    try {
      const response = await matchTerm(tenantId, invoiceId, { termId: term.id });
      onMatchChange(response.paymentTermsMatch);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to match term:', err);
      setError('Failed to match payment term');
      setShowError(true);
      setTimeout(() => setShowError(false), 2000);
    } finally {
      setIsMatching(false);
    }
  };

  const handleUnmatch = async () => {
    setIsMatching(true);
    setError(null);

    try {
      await unmatchTerm(tenantId, invoiceId);
      onMatchChange(undefined);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to unmatch term:', err);
      setError('Failed to remove match');
      setShowError(true);
      setTimeout(() => setShowError(false), 2000);
    } finally {
      setIsMatching(false);
    }
  };

  const handleCreateClick = () => {
    setIsOpen(false);
    onOpenCreateModal();
  };

  // Format term for display
  const formatTermDisplay = (term: TermListItem) => {
    return term.name;
  };

  // Determine display state
  // Check if we have valid extracted terms (not "Net null" or similar invalid values)
  const hasValidPaymentTerms =
    extractedTerms.paymentTerms &&
    !extractedTerms.paymentTerms.toLowerCase().includes('null') &&
    !extractedTerms.paymentTerms.toLowerCase().includes('undefined');
  const hasExtractedTerms = hasValidPaymentTerms || extractedTerms.paymentTermsDueDays;
  const isMatched = !!currentMatch;
  const matchedTerm = isMatched ? terms.find((t) => t.id === currentMatch.entityId) : null;

  // Status styles (matching EditableValue)
  const statusStyles = showSuccess ? 'border-primary-500' : showError ? 'border-red-500' : '';
  const savingStyles = isMatching
    ? 'border-primary-500 shadow-[0_0_0_3px_rgba(44,160,28,0.4)] animate-pulse'
    : '';
  const hasStatus = showSuccess || showError || isMatching;
  const borderStyle = hasStatus ? '' : 'border-transparent hover:border-gray-300';

  return (
    <div className="relative">
      {/* Current value display / dropdown trigger */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled || isLoading || isMatching}
        className={`w-full flex items-center py-1.5 px-2 text-sm font-medium rounded-md border transition-all duration-200 ${borderStyle} ${savingStyles} ${statusStyles} ${
          disabled || isLoading || isMatching ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        {/* Dropdown arrow on left */}
        <svg
          className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>

        {/* Value - right aligned */}
        <span className="flex-1 text-right truncate ml-1">
          {isLoading ? (
            <span className="text-gray-400">Loading...</span>
          ) : isMatched && matchedTerm ? (
            <span className={className || 'text-gray-700'}>{matchedTerm.name}</span>
          ) : hasExtractedTerms ? (
            <span className="text-gray-700">
              {hasValidPaymentTerms
                ? extractedTerms.paymentTerms
                : `${extractedTerms.paymentTermsDueDays} days`}
            </span>
          ) : (
            <span className="text-gray-400">Not found</span>
          )}
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />

          {/* Dropdown menu */}
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
            {/* Matched term with unmatch option */}
            {isMatched && (
              <div className="px-3 py-2 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 font-medium">
                    Matched: {currentMatch.entityName}
                  </span>
                  <button
                    type="button"
                    onClick={handleUnmatch}
                    disabled={isMatching}
                    className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}

            {/* Terms list */}
            {sortedTerms.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No payment terms available</div>
            ) : (
              sortedTerms.map((term) => (
                <button
                  key={term.id}
                  type="button"
                  onClick={() => handleSelect(term)}
                  disabled={isMatching || (isMatched && term.id === currentMatch.entityId)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 disabled:opacity-50 flex items-center justify-between"
                >
                  <span>{formatTermDisplay(term)}</span>
                  {suggestedTerm?.id === term.id && !isMatched && (
                    <span className="text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
                      Suggested
                    </span>
                  )}
                </button>
              ))
            )}

            {/* Add new term option */}
            <button
              type="button"
              onClick={handleCreateClick}
              className="w-full px-3 py-2 text-left text-sm text-primary-600 hover:bg-primary-50 border-t border-gray-100 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add payment term
            </button>
          </div>
        </>
      )}

      {/* Error message */}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
